import React, { useState, useEffect } from "react";
import moment from "moment";
import { API_GET } from "../../helpers/api";
import DataTableBasic from "../DataTable/DataTableBasic";
import {
  convertAmountToFrontend,
  convertNumberToFrontend,
} from "../../helpers/convert";

export default function ModalMatchHistory(props) {
  const { exchangeOrderMatch, t } = props;

  const [buyerDetails, setBuyerDetails] = useState([]);

  const [sellerDetails, setSellerDetails] = useState([]);

  const [sellHistory, setSellHistory] = useState([]);

  const [buyHistory, setBuyHistory] = useState([]);

  const [matchedSells, setMatchedSells] = useState([]);
  const [matchedBuys, setMatchedBuys] = useState([]);

  const orderInfoColumns = [
    {
      name: t("pages.default.objections.history.organizationName"),
      selector: "organizationName",
    },
    {
      name: t("pages.default.objections.history.userName"),
      selector: "userName",
    },
    {
      name: t("pages.default.objections.history.orderId"),
      selector: "orderId",
    },
  ];

  const orderHistoryColumns = [
    {
      name: t("pages.default.objections.history.lastModifiedDate"),
      selector: "updatedAt",
      width: "200px",
      cell: (row) => {
        return moment(row.createdDate).format("DD.MM.YYYY HH:mm:ss:SSS");
      },
    },
    {
      name: t("pages.default.objections.history.calculatedAmount"),
      selector: "calculatedAmount",
      cell: (row) => {
        return convertAmountToFrontend(row.calculatedAmount);
      },
    },
    {
      name: t("pages.default.objections.history.price"),
      selector: "price",
      cell: (row) => {
        return convertNumberToFrontend({ num: row.price, digit: 2 });
      },
    },
    {
      key: "matchedAmount",
      name: t("pages.default.objections.history.matchedAmount"),
      selector: "matchedAmount",
      cell: (row) => {
        return convertAmountToFrontend(row.matchedAmount);
      },
    },
    {
      name: t("pages.default.objections.history.calculatedRemainingAmount"),
      selector: "calculatedRemainingAmount",
      cell: (row) => {
        return convertAmountToFrontend(row.calculatedRemainingAmount);
      },
    },
    {
      name: t("pages.default.objections.history.status"),
      selector: "status",
      cell: (row) => {
        return t(`ui.default.matchHistory.${row.status}`);
      },
    },
  ];

  useEffect(() => {
    getExchangeOrderMatchHistory();
    // lastMatchId
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exchangeOrderMatch]);

  useEffect(() => {
    let bh = JSON.parse(JSON.stringify(buyHistory));
    let sh = JSON.parse(JSON.stringify(sellHistory));
    let matchedSell = bh.reduce((acc, value) => {
      let matched = sh.find(function (item) {
        if (
          item.lastMatchId === value.lastMatchId &&
          item.lastMatchId &&
          (item.status === "PARTIAL_MATCHED" || item.status === "FULLY_MATCHED")
        ) {
          return item;
        }
      });
      if (matched) acc.push(matched);
      return acc;
    }, []);
    let matchedBuy = sh.reduce((acc, value) => {
      let matched = bh.find(
        (item) =>
          item.lastMatchId === value.lastMatchId &&
          item.lastMatchId &&
          (item.status === "PARTIAL_MATCHED" || item.status === "FULLY_MATCHED")
      );
      if (matched) acc.push(matched);
      return acc;
    }, []);
    setMatchedBuys(matchedBuy);
    setMatchedSells(matchedSell);
  }, [buyHistory, sellHistory]);

  const statusCalculator = (value, prevValue) => {
    let status = "GROUP_UPDATED";
    if (!prevValue) {
      status = "CREATED";
    }
    if (
      prevValue &&
      (value.amount !== prevValue.amount || value.price !== prevValue.price)
    ) {
      status = "UPDATED";
    }
    if (value.matchedAmount > 0) {
      status = "PARTIAL_MATCHED";
    }
    if (value.remainingAmount === 0) {
      status = "FULLY_MATCHED";
    }

    if (value.status === "CANCELLED") {
      status = "UPDATED";
    }
    return status;
  };

  const getExchangeOrderMatchHistory = async () => {
    API_GET(
      `exchange-order/order-group-id/${exchangeOrderMatch?.details?.matchBundle?.sellOrderGroupId}/history`
    ).then((res) => {
      let sellData = res.data.reduce((acc, value, index) => {
        if (index === 0) {
          let matchedAmount;
          if (value.remainingAmount === 0) {
            matchedAmount = value.amount;
          } else {
            matchedAmount = value.amount - value.remainingAmount;
          }
          acc.push({
            ...value,
            calculatedAmount: value.amount,
            calculatedRemainingAmount: value.remainingAmount,
            matchedAmount: matchedAmount,
            status: statusCalculator({
              ...value,
              matchedAmount: matchedAmount,
            }),
          });
        } else {
          let cAmount = value.amount;
          let cRAmount = value.remainingAmount;
          if (cAmount)
            acc.push({
              ...value,
              calculatedAmount: cAmount,
              calculatedRemainingAmount: cRAmount,
              matchedAmount: cAmount - cRAmount,
              status: statusCalculator(
                {
                  ...value,
                  calculatedAmount: cAmount,
                  matchedAmount: cAmount - cRAmount,
                },
                res.data[index - 1]
              ),
            });
        }
        return acc;
      }, []);
      setSellHistory(sellData);
      let sDetails = [
        {
          organizationName: res.data[0].organizationName,
          userName: res.data[0].createdBy,
          orderId: res.data[0].orderGroupDto.id,
        },
      ];
      setSellerDetails(sDetails);
    });
    API_GET(
      `exchange-order/order-group-id/${exchangeOrderMatch?.details?.matchBundle?.buyOrderGroupId}/history`
    ).then((res) => {
      let buyData = res.data.reduce((acc, value, index) => {
        if (index === 0) {
          let matchedAmount;
          if (value.remainingAmount === 0) {
            matchedAmount = value.amount;
          } else {
            matchedAmount = value.amount - value.remainingAmount;
          }
          acc.push({
            ...value,
            calculatedAmount: value.amount,
            calculatedRemainingAmount: value.remainingAmount,
            matchedAmount: matchedAmount,
            status: statusCalculator({
              ...value,
              matchedAmount: matchedAmount,
            }),
          });
        } else {
          let cAmount = value.amount;
          let cRAmount = value.remainingAmount;
          if (cAmount)
            acc.push({
              ...value,
              calculatedAmount: cAmount,
              calculatedRemainingAmount: cRAmount,
              matchedAmount: cAmount - cRAmount,
              status: statusCalculator(
                {
                  ...value,
                  calculatedAmount: cAmount,
                  matchedAmount: cAmount - cRAmount,
                },
                res.data[index - 1]
              ),
            });
        }
        return acc;
      }, []);
      setBuyHistory(buyData);
      let bDetails = [
        {
          organizationName: res.data[0].organizationName,
          userName: res.data[0].createdBy,
          orderId: res.data[0].orderGroupDto.id,
        },
      ];
      setBuyerDetails(bDetails);
    });
  };

  const isRowBlue = (row, direction) => {
    // eşleşme varsa
    if (row.lastMatchId && (matchedSells || matchedBuys)) {
      // satış
      if (direction === "sell") {
        let b = matchedBuys.find(function (item) {
          return item.lastMatchId === row.lastMatchId;
        });
        return !!b;
      }
      // alış
      else {
        let b = matchedSells.find(function (item) {
          return item.lastMatchId === row.lastMatchId;
        });
        return !!b;
      }
    }
    return false;
  };
  return (
    <div className="km__p--order-history-wrapper">
      <div className="km__p--order-history-wrapper-column">
        <p className="km__u--text-green-300">
          {t("pages.default.marketResults.buy")}
        </p>
        <div className="km__p--order-history-wrapper-order-details">
          <DataTableBasic
            rowKey="orderId"
            columns={orderInfoColumns}
            data={buyerDetails}
          />
        </div>
        <div className="km__p--order-history-wrapper-history-details">
          <DataTableBasic
            rowKey="updatedAt"
            columns={orderHistoryColumns}
            data={buyHistory.filter((item) => item.status !== "GROUP_UPDATED")}
            blueRowDirection="buy"
            isRowBlue={isRowBlue}
          />
        </div>
      </div>
      <div className="km__p--order-history-wrapper-column">
        <p className="km__u--text-red-300">
          {t("pages.default.marketResults.sell")}
        </p>
        <div className="km__p--order-history-wrapper-order-details">
          <DataTableBasic
            rowKey="orderId"
            columns={orderInfoColumns}
            data={sellerDetails}
          />
        </div>
        <div className="km__p--order-history-wrapper-history-details">
          <DataTableBasic
            rowKey="updatedAt"
            columns={orderHistoryColumns}
            data={sellHistory.filter((item) => item.status !== "GROUP_UPDATED")}
            blueRowDirection="sell"
            isRowBlue={isRowBlue}
          />
        </div>
      </div>
    </div>
  );
}
